<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { CoreTypes } from '@vp/shared-types';
import { exportImage, type ExportScope } from '../export';
import { live, ui } from '../editor/bridge';
import { t } from '../i18n';
import { settings } from '../platform';
import Icon from './Icon.vue';

/** PNG / SVG / PDF export (§13.1): selection, a frame (cropped to it), the view or the whole canvas; PDF also every frame as a page. */
const props = defineProps<{ format: 'png' | 'svg' | 'pdf'; initialScope?: ExportScope }>();
const emit = defineEmits<{ close: [] }>();

const frames = computed(() => {
  void ui.docRev;
  const ed = live.editor;
  if (!ed) return [];
  return [...ed.doc.allObjects()]
    .filter((o) => o.type === CoreTypes.frame)
    .map((o) => ({ id: o.id, title: (o.props['title'] as string) || t('props.frameUntitled') }));
});

/** A single selected frame exports as that frame by default. */
function defaultScope(): ExportScope {
  if (props.initialScope && props.initialScope !== 'selection') return props.initialScope;
  const sel = ui.selection;
  const one = sel.length === 1 ? live.editor?.doc.get(sel[0]) : undefined;
  if (one?.type === CoreTypes.frame) return `frame:${one.id}`;
  return sel.length ? 'selection' : 'all';
}

const format = ref<'png' | 'svg' | 'pdf'>(props.format);
const scope = ref<ExportScope>(defaultScope());
const scale = ref<1 | 2 | 3>(2);
const bgMode = ref<'transparent' | 'color'>('color');
const bg = ref(settings.background);
const unavailable = ref<'placeholder' | 'skip'>('placeholder');
const error = ref<string | null>(null);
const busy = ref(false);
const box = ref<HTMLElement | null>(null);

onMounted(() => (box.value?.querySelector('button') as HTMLElement | null)?.focus());

async function go(): Promise<void> {
  busy.value = true;
  error.value = await exportImage({
    format: format.value,
    scope: scope.value,
    scale: scale.value,
    background: bgMode.value === 'color' ? bg.value : null,
    unavailable: unavailable.value,
  });
  busy.value = false;
  if (!error.value) emit('close');
}

const scopes = computed(() => [
  { id: 'selection' as ExportScope, label: t('export.selection'), icon: 'select', disabled: !ui.selection.length },
  { id: 'view' as ExportScope, label: t('export.view'), icon: 'fit', disabled: false },
  { id: 'all' as ExportScope, label: t('export.all'), icon: 'grid', disabled: false },
  ...(format.value === 'pdf' && frames.value.length > 1 ? [{ id: 'frames' as ExportScope, label: t('export.framePages'), icon: 'frame', disabled: false }] : []),
]);
// a page per frame exists only for PDF
watch(format, (f) => {
  if (f !== 'pdf' && scope.value === 'frames') scope.value = 'all';
});
</script>

<template>
  <div class="dialog-backdrop" @click.self="emit('close')" @keydown.esc="emit('close')">
    <div ref="box" class="box" role="dialog" aria-modal="true" :aria-label="t('export.title')">
      <header>
        <h3>{{ t('export.title') }}</h3>
        <button class="icon-btn" :aria-label="t('common.close')" @click="emit('close')"><Icon name="close" :size="16" /></button>
      </header>

      <p class="sec">{{ t('export.format') }}</p>
      <div class="seg-pills" role="radiogroup" :aria-label="t('export.format')">
        <button v-for="f in ['png', 'svg', 'pdf'] as const" :key="f" role="radio" :aria-checked="format === f" :class="{ active: format === f }" @click="format = f">{{ f.toUpperCase() }}</button>
      </div>

      <p class="sec">{{ t('export.scope') }}</p>
      <div class="scopes" role="radiogroup" :aria-label="t('export.scope')">
        <button
          v-for="s in scopes"
          :key="s.id"
          role="radio"
          class="scope"
          :class="{ active: scope === s.id }"
          :aria-checked="scope === s.id"
          :disabled="s.disabled"
          @click="scope = s.id"
        >
          <Icon :name="s.icon" :size="15" />{{ s.label }}
        </button>
      </div>
      <template v-if="frames.length">
        <p class="sec" :title="t('export.frameHint')">{{ t('export.frames') }}</p>
        <div class="scopes frames" role="radiogroup" :aria-label="t('export.frames')">
          <button
            v-for="f in frames"
            :key="f.id"
            role="radio"
            class="scope"
            :class="{ active: scope === 'frame:' + f.id }"
            :aria-checked="scope === 'frame:' + f.id"
            :title="t('export.frameHint')"
            @click="scope = `frame:${f.id}`"
          >
            <Icon name="frame" :size="15" /><span class="name">{{ f.title }}</span>
          </button>
        </div>
      </template>

      <div class="row2">
        <div v-if="format === 'png'">
          <p class="sec">{{ t('export.scale') }}</p>
          <div class="seg-pills" role="radiogroup" :aria-label="t('export.scale')">
            <button v-for="k in [1, 2, 3] as const" :key="k" role="radio" :aria-checked="scale === k" :class="{ active: scale === k }" @click="scale = k">{{ k }}x</button>
          </div>
        </div>
        <div>
          <p class="sec">{{ t('export.background') }}</p>
          <div class="inline">
            <div class="seg-pills" role="radiogroup" :aria-label="t('export.background')">
              <button role="radio" :aria-checked="bgMode === 'transparent'" :class="{ active: bgMode === 'transparent' }" @click="bgMode = 'transparent'">{{ t('export.transparent') }}</button>
              <button role="radio" :aria-checked="bgMode === 'color'" :class="{ active: bgMode === 'color' }" @click="bgMode = 'color'">{{ t('export.color') }}</button>
            </div>
            <label v-if="bgMode === 'color'" class="swatch-btn color" :style="{ background: bg }" :title="t('export.color')">
              <input v-model="bg" type="color" :aria-label="t('export.color')" />
            </label>
          </div>
        </div>
      </div>

      <p class="sec">{{ t('export.unavailable') }}</p>
      <div class="seg-pills" role="radiogroup" :aria-label="t('export.unavailable')">
        <button role="radio" :aria-checked="unavailable === 'placeholder'" :class="{ active: unavailable === 'placeholder' }" @click="unavailable = 'placeholder'">{{ t('export.placeholder') }}</button>
        <button role="radio" :aria-checked="unavailable === 'skip'" :class="{ active: unavailable === 'skip' }" @click="unavailable = 'skip'">{{ t('export.skip') }}</button>
      </div>

      <p v-if="error" class="err" role="alert">{{ error }}</p>
      <div class="actions">
        <button class="pill" @click="emit('close')">{{ t('export.cancel') }}</button>
        <button class="pill primary" :disabled="busy || !live.editor" @click="go"><Icon name="download" :size="14" />{{ t('export.go') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.box {
  width: min(460px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 18px 20px 16px;
  background: var(--panel);
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.22);
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sec {
  margin: 14px 0 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-3);
}
.seg-pills button {
  padding: 0 12px;
}
.scopes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.scopes.frames {
  max-height: 120px;
  overflow: auto;
}
.scope {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
  color: var(--text-2);
  font-size: 12.5px;
  cursor: pointer;
  max-width: 100%;
}
.scope:hover:not(:disabled) {
  background: var(--panel-2);
}
.scope.active {
  background: var(--accent-soft);
  border-color: var(--accent-border);
  color: var(--accent-text);
}
.scope:disabled {
  opacity: 0.4;
  cursor: default;
}
.scope .name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row2 {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.inline {
  display: flex;
  align-items: center;
  gap: 8px;
}
.color {
  overflow: hidden;
}
.color input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.err {
  color: var(--danger);
  font-size: 12px;
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
.actions .pill {
  height: 34px;
  padding: 0 16px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover:not(:disabled) {
  background: var(--accent-2);
  color: var(--on-accent);
}
</style>
